import { useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Home, Star, Tv, Play, X, Award, Eye, Heart, Sparkles, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import { getTVDetailsEnriched, type OMDBEnrichment } from '../api/tmdb';
import { getPosterUrl, getBackdropUrl, calculateOurRating, getProfileUrl, getComingOnLabel } from '../utils/helpers';
import { queryKeys } from '../lib/queryKeys';
import { TV_GENRES } from '../utils/constants';
import { useUserLibrary } from '../contexts/UserLibraryContext';
import { useAuth } from '../contexts/AuthContext';
import { useUserRating } from '../hooks/useRatings';
import { RatingModal } from './RatingModal';
import { RecommendModal } from './RecommendModal';
import { AddToListDropdown } from './AddToListDropdown';
import { ShareButton } from './ShareButton';
import { useToast } from './ui/Toast';
import { TVDetailSkeleton } from './ui/Skeleton';
import { MovieCard } from './MovieCard';
import { JustWatchWidget } from './JustWatchWidget';
import type { TVShow, CastMember, CrewMember, MediaItem, SimilarItem } from '../types/movie';

// =============================================================================
// SIMILAR SHELF COMPONENT (For You shelf mechanics)
// =============================================================================

interface SimilarShelfProps {
  similar: SimilarItem[] | undefined;
  navigate: ReturnType<typeof useNavigate>;
}

function SimilarShelf({ similar, navigate }: SimilarShelfProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Drag-to-scroll: use refs for pointer values (avoids stale closures in handlers)
  const [isDragging, setIsDragging] = useState(false);
  const isTrackingRef = useRef(false);
  const isDraggingRef = useRef(false);
  const hasCapturedRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const dragDistanceRef = useRef(0);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Pointer drag-to-scroll handlers
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!scrollRef.current) return;

    // Ignore if pointerdown originates from interactive element
    const target = e.target as HTMLElement;
    if (target.closest('button, a, input, textarea, select, [role="button"], [role="menuitem"]')) {
      return;
    }

    isTrackingRef.current = true;
    isDraggingRef.current = false;
    hasCapturedRef.current = false;
    startXRef.current = e.clientX;
    scrollLeftRef.current = scrollRef.current.scrollLeft;
    dragDistanceRef.current = 0;
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isTrackingRef.current || !scrollRef.current) return;

    const dx = e.clientX - startXRef.current;
    dragDistanceRef.current = Math.abs(dx);

    // Only capture pointer and start dragging after threshold exceeded (3px)
    if (!hasCapturedRef.current && dragDistanceRef.current > 3) {
      try {
        scrollRef.current.setPointerCapture(e.pointerId);
        hasCapturedRef.current = true;
      } catch {
        // Pointer may have been released
      }
      isDraggingRef.current = true;
      setIsDragging(true);
    }

    if (isDraggingRef.current) {
      scrollRef.current.scrollLeft = scrollLeftRef.current - dx;
    }
  };

  const handlePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    isTrackingRef.current = false;
    isDraggingRef.current = false;
    setIsDragging(false);

    if (hasCapturedRef.current && scrollRef.current) {
      try {
        scrollRef.current.releasePointerCapture(e.pointerId);
      } catch {
        // Browser quirks
      }
    }
    hasCapturedRef.current = false;
  };

  // Prevent navigation clicks after drag (threshold: 5px)
  const handleSimilarClick = (item: MediaItem) => {
    if (dragDistanceRef.current > 5) {
      return;
    }
    navigate(`/${item.media_type}/${item.id}`);
  };

  if (!similar || similar.length === 0) return null;

  // Map SimilarItem[] to MediaItem[] with real values from backend (cap to 20)
  const similarMediaItems: MediaItem[] = similar.slice(0, 20).map((sim) => ({
    id: sim.id,
    media_type: sim.media_type,
    title: sim.title || '',
    overview: '',
    poster_path: sim.poster_path,
    backdrop_path: null,
    release_date: sim.release_date || '',
    genre_ids: sim.genre_ids ?? [],
    vote_average: sim.vote_average ?? 0,
    vote_count: sim.vote_count ?? 0,
    popularity: sim.popularity ?? 0,
    original_language: sim.original_language ?? '',
    runtime: sim.runtime ?? undefined,
    imdb_rating: sim.imdb_rating ?? undefined,
    rt_rating: sim.rt_rating ?? undefined,
    filmpl_rating: sim.filmpl_rating ?? undefined,
    number_of_seasons: sim.number_of_seasons ?? undefined,
  }));

  return (
    <div className="mt-12 mb-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-white">You Might Enjoy</h2>
      </div>
      <div className="relative">
        {/* Left Arrow */}
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-[#0A0A0A]/80 hover:bg-black rounded-full flex items-center justify-center text-white opacity-30 hover:opacity-100 transition-opacity -ml-2"
        >
          <ChevronLeft size={24} />
        </button>

        {/* Movies Row with drag-to-scroll */}
        <div
          ref={scrollRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerLeave={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          className={`flex gap-4 overflow-x-auto scrollbar-hide pb-2 select-none ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          style={{ touchAction: 'pan-y' }}
        >
          {similarMediaItems.map((item) => (
            <div key={`${item.media_type}-${item.id}`} className="flex-shrink-0 w-[160px]">
              <MovieCard
                movie={item}
                onClick={handleSimilarClick}
                showQuickActions={true}
                imagePriority="low"
              />
            </div>
          ))}
        </div>

        {/* Right Arrow */}
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-[#0A0A0A]/80 hover:bg-black rounded-full flex items-center justify-center text-white opacity-30 hover:opacity-100 transition-opacity -mr-2"
        >
          <ChevronRight size={24} />
        </button>
      </div>
    </div>
  );
}

interface VideoResult {
  id: string;
  key: string;
  name: string;
  site: string;
  type: string;
}

interface TVDetails extends TVShow {
  tagline?: string;
  networks?: { id: number; name: string; logo_path: string | null }[];
  production_companies?: { id: number; name: string; logo_path: string | null }[];
  spoken_languages?: { english_name: string; iso_639_1: string }[];
  credits?: {
    cast: CastMember[];
    crew: CrewMember[];
  };
  videos?: {
    results: VideoResult[];
  };
  omdb?: OMDBEnrichment;
}

export function TVDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tvId = parseInt(id || '0', 10);
  const [showTrailer, setShowTrailer] = useState(false);

  // React Query for TV data - route-keyed to prevent stale renders
  const {
    data: show,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: queryKeys.tv.detail(tvId),
    queryFn: () => getTVDetailsEnriched(tvId) as Promise<TVDetails>,
    enabled: tvId > 0,
  });

  const error = queryError ? 'Failed to load TV show details' : null;

  // Watched tracking - uses optimistic UI from UserLibraryContext
  const { user } = useAuth();
  const { toast } = useToast();
  const { isWatched: checkIsWatched, toggleWatched: optimisticToggleWatched, isInFavorites, toggleFavorites } = useUserLibrary();
  const isLoggedIn = !!user;
  const isWatched = checkIsWatched(tvId, 'tv');
  const isFavorite = isInFavorites(tvId, 'tv');

  // Toggle watched with show info for optimistic UI
  const toggleWatched = async () => {
    if (!show) return;
    const wasWatched = isWatched;
    const year = show.first_air_date ? parseInt(show.first_air_date.slice(0, 4)) : undefined;
    try {
      const result = await optimisticToggleWatched(tvId, 'tv', show.name || '', show.poster_path || undefined, year);
      // result is null if superseded (user toggled again quickly) - ignore silently
      if (result) {
        toast.success(wasWatched ? 'Removed from watched' : 'Marked as watched');
      }
    } catch (err) {
      console.error('Error toggling watched:', err);
      toast.error('Something went wrong');
    }
  };

  // Toggle favorites with toast feedback
  const handleToggleFavorites = async () => {
    if (!show) return;
    const wasFav = isFavorite;
    const year = show.first_air_date ? parseInt(show.first_air_date.slice(0, 4)) : undefined;
    try {
      const result = await toggleFavorites(show.id, 'tv', show.name, show.poster_path || undefined, year);
      // result is null if superseded - ignore silently
      if (result) {
        toast.success(wasFav ? 'Removed from Favorites' : 'Added to Favorites');
      }
    } catch (err) {
      console.error('Error toggling favorites:', err);
      toast.error('Something went wrong');
    }
  };

  // Ratings - React Query (single source of truth)
  const { rating: userRating } = useUserRating(tvId, 'tv');
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [showRecommendModal, setShowRecommendModal] = useState(false);

  // Skeleton while loading (no full-page block if cached)
  if (loading && !show) {
    return <TVDetailSkeleton />;
  }

  // Error state (only if no show and not loading)
  if (!show) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || 'TV show not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const creators = show.created_by || [];
  const cast = show.credits?.cast.slice(0, 12) || [];
  const ourRating = calculateOurRating(show.imdb_rating, show.rt_rating);
  const year = show.first_air_date?.split('-')[0];
  const avgRuntime = show.runtime ?? show.episode_run_time?.[0];

  // Find trailer from videos - prioritize Trailer/Teaser, fall back to any YouTube video
  const youtubeVideos = show.videos?.results.filter((v) => v.site === 'YouTube') || [];
  const trailerKey = youtubeVideos.find((v) => v.type === 'Trailer' || v.type === 'Teaser')?.key
    || youtubeVideos.find((v) => v.type === 'Clip')?.key
    || youtubeVideos[0]?.key;

  return (
    <div className="min-h-screen bg-[#111111] relative">
      {/* Hero Section with Backdrop - extends behind TopBar */}
      <div className="relative h-[400px] w-full overflow-hidden -mt-16">
        {/* Navigation buttons - top left */}
        <div className="absolute top-20 left-4 z-20 flex gap-2">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-3 py-2 bg-[#0F1419]/80 backdrop-blur-sm rounded-lg text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Back</span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-3 py-2 bg-[#0F1419]/80 backdrop-blur-sm rounded-lg text-slate-300 hover:text-white transition-colors"
          >
            <Home size={18} />
            <span className="text-sm font-medium">Home</span>
          </button>
        </div>
        {show.backdrop_path ? (
          <img
            src={getBackdropUrl(show.backdrop_path, 'large')}
            alt={show.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-900" />
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/60 to-transparent" />
      </div>

      {/* Trailer Modal */}
      {showTrailer && trailerKey && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#0A0A0A]/90 backdrop-blur-sm"
          onClick={() => setShowTrailer(false)}
        >
          <div className="relative w-full max-w-5xl mx-4 aspect-video">
            <button
              onClick={() => setShowTrailer(false)}
              className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
            >
              <X size={28} />
            </button>
            <iframe
              src={`https://www.youtube.com/embed/${trailerKey}?autoplay=1`}
              className="w-full h-full rounded-lg"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="px-6 pb-12 -mt-48 relative z-10">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Poster */}
          <div className="flex-shrink-0">
            <div className="w-[200px] aspect-[2/3] rounded-lg overflow-hidden shadow-xl bg-[#1A1F26]">
              {show.poster_path ? (
                <img
                  src={getPosterUrl(show.poster_path, 'large')}
                  alt={show.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Tv size={48} className="text-slate-600" />
                </div>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="flex-1">
            {/* Title + Year */}
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-1">
              {show.name}
              {year && <span className="text-slate-400 font-normal ml-2">({year})</span>}
            </h1>

            {/* Coming on date for unreleased TV shows */}
            {getComingOnLabel(show.first_air_date) && (
              <p className="text-teal-400 text-sm font-medium mb-2">Coming on {getComingOnLabel(show.first_air_date)}</p>
            )}

            {/* Tagline */}
            {show.tagline && (
              <p className="text-slate-400 italic mb-4">"{show.tagline}"</p>
            )}

            {/* Rating Badges - Horizontal pills */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {ourRating && (
                <div className="flex items-center gap-1.5 px-3 py-1 bg-purple-500 rounded-full">
                  <Sparkles size={14} className="text-purple-200" />
                  <span className="text-sm font-semibold text-white">{ourRating.toFixed(1)}</span>
                  <span className="text-xs font-medium text-purple-200 ml-0.5">FILMPLE</span>
                </div>
              )}
              {show.imdb_rating && (
                <div className="flex items-center gap-1.5 px-3 py-1 bg-[#f5c518] rounded-full">
                  <Star size={14} className="text-yellow-600 fill-yellow-600" />
                  <span className="text-sm font-semibold text-black">{show.imdb_rating.toFixed(1)}</span>
                  <span className="text-xs font-medium text-yellow-800 ml-0.5">IMDB</span>
                </div>
              )}
              {show.rt_rating && (
                <div className="flex items-center gap-1.5 px-3 py-1 bg-[#fa320a] rounded-full">
                  <span className="text-sm">🍅</span>
                  <span className="text-sm font-semibold text-white">{show.rt_rating}%</span>
                  <span className="text-xs font-medium text-red-200 ml-0.5">RT</span>
                </div>
              )}
            </div>

            {/* Metadata Row - Genres + Seasons + Runtime + Status separated by dots */}
            <div className="flex flex-wrap items-center gap-x-2 text-sm text-slate-400 mb-6">
              {show.genre_ids && show.genre_ids.length > 0 && (
                <>
                  <span>{show.genre_ids.map(id => TV_GENRES[id]).filter(Boolean).join(' • ')}</span>
                  <span className="text-slate-600">•</span>
                </>
              )}
              {show.number_of_seasons && (
                <>
                  <span>{show.number_of_seasons} Season{show.number_of_seasons > 1 ? 's' : ''}</span>
                  <span className="text-slate-600">•</span>
                </>
              )}
              {avgRuntime && (
                <>
                  <span>{avgRuntime} min/ep</span>
                  <span className="text-slate-600">•</span>
                </>
              )}
              {show.status && (
                <span className={
                  show.status === 'Returning Series' ? 'text-green-400' :
                  show.status === 'Ended' ? 'text-red-400' :
                  'text-slate-400'
                }>
                  {show.status}
                </span>
              )}
            </div>

            {/* Action Buttons Row */}
            <div className="flex flex-wrap items-center gap-3 mb-8">
              {/* Play Trailer */}
              {trailerKey && (
                <button
                  onClick={() => setShowTrailer(true)}
                  className="flex items-center gap-2 h-10 px-4 rounded-lg border border-teal-500 text-teal-400 hover:bg-teal-500/10 transition-colors font-medium"
                >
                  <Play size={18} className="fill-current" />
                  Play Trailer
                </button>
              )}

              {isLoggedIn && (
                <>
                  {/* Add to Watchlist */}
                  {/* TASK A: Pass enrichment for instant metadata display on WatchlistPage */}
                  <AddToListDropdown
                    mediaId={show.id}
                    mediaType="tv"
                    mediaTitle={show.name}
                    mediaPoster={show.poster_path ?? undefined}
                    mediaYear={year ? parseInt(year) : undefined}
                    enrichment={{
                      genre_ids: show.genre_ids,
                      runtime: avgRuntime,
                      imdb_rating: show.imdb_rating,
                      rt_rating: show.rt_rating,
                      filmpl_rating: ourRating ?? undefined,
                      popularity: show.popularity,
                      original_language: show.original_language,
                      vote_count: show.vote_count,
                    }}
                  />

                  {/* Watched Button */}
                  <button
                    onClick={toggleWatched}
                    className={`flex items-center gap-2 h-10 px-4 rounded-lg font-medium transition-all ${
                      isWatched
                        ? 'bg-teal-500 text-white hover:bg-teal-600'
                        : 'ring-1 ring-white/[0.08] text-slate-300 hover:ring-white/[0.12] hover:text-white'
                    }`}
                  >
                    <Eye size={18} />
                    {isWatched ? 'Watched' : 'Mark Watched'}
                  </button>

                  {/* Heart/Favorite Button */}
                  <button
                    onClick={handleToggleFavorites}
                    className={`flex items-center justify-center h-10 w-10 rounded-lg border transition-colors ${
                      isFavorite
                        ? 'bg-red-500/20 border-red-500 text-red-500'
                        : 'ring-1 ring-white/[0.08] text-slate-400 hover:ring-red-500 hover:text-red-500'
                    }`}
                  >
                    <Heart size={18} fill={isFavorite ? 'currentColor' : 'none'} />
                  </button>

                  {/* Rate Button */}
                  <button
                    onClick={() => setIsRatingModalOpen(true)}
                    className={`flex items-center gap-2 h-10 px-4 rounded-lg font-medium transition-all ${
                      userRating
                        ? 'bg-purple-500/20 border border-purple-500 text-purple-400'
                        : 'ring-1 ring-white/[0.08] text-slate-300 hover:ring-white/[0.12] hover:text-white'
                    }`}
                  >
                    <Star size={18} className={userRating ? 'fill-current' : ''} />
                    {userRating ? `${userRating}/10` : 'Rate'}
                  </button>

                  {/* Recommend Button */}
                  <button
                    onClick={() => setShowRecommendModal(true)}
                    className="flex items-center gap-2 h-10 px-4 rounded-lg font-medium ring-1 ring-white/[0.08] text-slate-300 hover:ring-white/[0.12] hover:text-white transition-all"
                  >
                    <Send size={18} />
                    Recommend
                  </button>
                </>
              )}

              {/* Share Button - available to everyone */}
              <ShareButton
                path={`/tv/${show.id}`}
                title={`${show.name}${year ? ` (${year})` : ''}`}
                image={show.poster_path ? getPosterUrl(show.poster_path, 'large') : undefined}
              />
            </div>

            {/* Overview */}
            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-3">Overview</h2>
              <p className="text-slate-300 leading-relaxed">{show.overview}</p>
            </div>

            {/* Creators */}
            {creators.length > 0 && (
              <div className="mb-10">
                <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">
                  Created By
                </h3>
                <div className="flex flex-wrap gap-3">
                  {creators.map((creator) => (
                    <Link
                      key={creator.id}
                      to={`/person/${creator.id}`}
                      className="flex items-center gap-3 p-2 rounded-lg bg-[#1A1F26]/50 hover:bg-[#1A1F26] transition-colors group"
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0">
                        {creator.profile_path ? (
                          <img
                            src={getProfileUrl(creator.profile_path, 'small')}
                            alt={creator.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-500">
                            <span className="text-xl">👤</span>
                          </div>
                        )}
                      </div>
                      <span className="text-white group-hover:text-teal-400 transition-colors font-medium">
                        {creator.name}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <JustWatchWidget
          mediaType="show"
          tmdbId={show.id}
          imdbId={show.imdb_id}
          title={show.name}
          year={year ? parseInt(year, 10) : undefined}
        />

        {/* Cast Section - Horizontal Scroll */}
        {cast.length > 0 && (
          <div className="mt-12 p-6 rounded-xl border border-teal-500/30 bg-[#0F1419]/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <div className="w-1 h-5 bg-teal-500 rounded-full" />
                Cast
              </h2>
              <Link to="#" className="flex items-center gap-1 text-sm text-teal-400 hover:text-teal-300">
                See All <ChevronRight size={16} />
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
              {cast.map((actor) => (
                <Link
                  key={actor.id}
                  to={`/person/${actor.id}`}
                  className="flex-shrink-0 w-20 text-center group"
                >
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-[#1A1F26] mb-2 mx-auto">
                    {actor.profile_path ? (
                      <img
                        src={`https://image.tmdb.org/t/p/w185${actor.profile_path}`}
                        alt={actor.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-600">
                        <span className="text-2xl">👤</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-white group-hover:text-teal-400 transition-colors truncate">
                    {actor.name}
                  </h3>
                  <p className="text-xs text-slate-500 truncate">{actor.character}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* You Might Enjoy - Similar Shows (For You shelf mechanics) */}
        <SimilarShelf
          similar={show.similar}
          navigate={navigate}
        />

        {/* Details Card - Redesigned with teal accent */}
        <div className="mt-12 p-6 rounded-xl border border-teal-500/30 bg-[#0F1419]/50">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <div className="w-1 h-5 bg-teal-500 rounded-full" />
            Details
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-6">
            {/* First Air Date */}
            {show.first_air_date && (
              <div>
                <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  First Aired
                </div>
                <p className="text-white text-sm">
                  {new Date(show.first_air_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            )}

            {/* Seasons */}
            {show.number_of_seasons && (
              <div>
                <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Seasons
                </div>
                <p className="text-white text-sm">{show.number_of_seasons} Season{show.number_of_seasons > 1 ? 's' : ''}</p>
              </div>
            )}

            {/* Episodes */}
            {show.number_of_episodes && (
              <div>
                <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                  </svg>
                  Episodes
                </div>
                <p className="text-white text-sm">{show.number_of_episodes} Episodes</p>
              </div>
            )}

            {/* Episode Runtime */}
            {avgRuntime && (
              <div>
                <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Episode Runtime
                </div>
                <p className="text-white text-sm">{avgRuntime} minutes</p>
              </div>
            )}

            {/* Language */}
            {show.original_language && (
              <div>
                <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  Language
                </div>
                <p className="text-white text-sm">
                  {(() => {
                    const langs: Record<string, string> = {
                      en: 'English', es: 'Spanish', fr: 'French', de: 'German',
                      it: 'Italian', ja: 'Japanese', ko: 'Korean', zh: 'Chinese',
                      pt: 'Portuguese', ru: 'Russian', hi: 'Hindi', ar: 'Arabic'
                    };
                    return langs[show.original_language] || show.original_language.toUpperCase();
                  })()}
                </p>
              </div>
            )}

            {/* Status */}
            {show.status && (
              <div>
                <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Status
                </div>
                <p className={`text-sm ${
                  show.status === 'Returning Series' ? 'text-green-400' :
                  show.status === 'Ended' ? 'text-red-400' :
                  'text-white'
                }`}>
                  {show.status}
                </p>
              </div>
            )}

            {/* Networks */}
            {show.networks && show.networks.length > 0 && (
              <div>
                <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Network
                </div>
                <p className="text-white text-sm">{show.networks.map(n => n.name).join(', ')}</p>
              </div>
            )}

            {/* Awards */}
            {show.omdb?.awards && show.omdb.awards !== 'N/A' && (
              <div className="col-span-2 md:col-span-3">
                <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                  <Award size={14} />
                  Awards
                </div>
                <p className="text-white text-sm">{show.omdb.awards}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rating Modal */}
      {show && (
        <RatingModal
          movie={{
            id: show.id,
            title: show.name,
            poster_path: show.poster_path,
            backdrop_path: show.backdrop_path,
            release_date: show.first_air_date,
            media_type: 'tv',
          } as MediaItem}
          isOpen={isRatingModalOpen}
          onClose={() => setIsRatingModalOpen(false)}
        />
      )}

      {/* Recommend Modal */}
      {show && (
        <RecommendModal
          isOpen={showRecommendModal}
          onClose={() => setShowRecommendModal(false)}
          media={{
            mediaId: show.id,
            mediaType: 'tv',
            title: show.name,
            poster: show.poster_path || undefined,
            year: year ? parseInt(year) : undefined,
          }}
        />
      )}
    </div>
  );
}
